import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { MistranslationReport, ProfileId } from '@cixing/shared';

/**
 * 講義履歴の保存先。
 *
 * サーバーには標準では保存しない。講義内容は顧客先の情報であり、
 * 端末内に留めるのが既定として安全なため。エクスポートは利用者が明示的に行う。
 */

export interface SubtitleRecord {
  segmentId: string;
  sessionId: string;
  clientSessionId: string;
  startedAt: string;
  sourceZh: string;
  translatedJa: string;
  profiles: ProfileId[];
  glossaryVersion: string;
}

interface CixingDb extends DBSchema {
  subtitles: {
    key: string;
    value: SubtitleRecord;
    indexes: { 'by-session': string; 'by-time': string };
  };
  reports: {
    key: string;
    value: MistranslationReport;
    indexes: { 'by-synced': string };
  };
  settings: {
    key: string;
    value: unknown;
  };
}

const DB_NAME = 'cixing-translate';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<CixingDb>> | undefined;

function db(): Promise<IDBPDatabase<CixingDb>> {
  dbPromise ??= openDB<CixingDb>(DB_NAME, DB_VERSION, {
    upgrade(database) {
      const subtitles = database.createObjectStore('subtitles', { keyPath: 'segmentId' });
      subtitles.createIndex('by-session', 'clientSessionId');
      subtitles.createIndex('by-time', 'startedAt');

      const reports = database.createObjectStore('reports', { keyPath: 'id' });
      // 未送信の報告を後からまとめて再送するために索引を張る。
      reports.createIndex('by-synced', 'synced');

      database.createObjectStore('settings');
    },
  });
  return dbPromise;
}

/**
 * 確定した字幕を保存する。
 * segmentId が主キーなので、同じセグメントの更新は自動的に上書きになる。
 */
export async function saveSubtitle(record: SubtitleRecord): Promise<void> {
  const database = await db();
  await database.put('subtitles', record);
}

/** 時系列順の全履歴。ページ再読み込み後の復元に使う。 */
export async function loadSubtitles(): Promise<SubtitleRecord[]> {
  const database = await db();
  const all = await database.getAll('subtitles');
  return all.sort((a, b) => (a.startedAt < b.startedAt ? -1 : a.startedAt > b.startedAt ? 1 : 0));
}

export async function clearSubtitles(): Promise<void> {
  const database = await db();
  await database.clear('subtitles');
}

/**
 * 指定日時より古い字幕を削除する。
 * 削除処理が無いと履歴が際限なく増え、起動時の復元(getAll)がどんどん
 * 重くなる(数万件で端末が固まる事故が実際に起きた)。確定字幕は
 * サーバー側の講義メモに永続化済みなので、端末には直近分だけあればよい。
 */
export async function pruneSubtitlesBefore(isoDate: string): Promise<number> {
  const database = await db();
  const tx = database.transaction('subtitles', 'readwrite');
  const index = tx.store.index('by-time');
  let deleted = 0;
  let cursor = await index.openCursor(IDBKeyRange.upperBound(isoDate, true));
  while (cursor) {
    await cursor.delete();
    deleted += 1;
    cursor = await cursor.continue();
  }
  await tx.done;
  return deleted;
}

export async function saveReport(report: MistranslationReport): Promise<void> {
  const database = await db();
  await database.put('reports', report);
}

export async function loadReports(): Promise<MistranslationReport[]> {
  const database = await db();
  return database.getAll('reports');
}

/** サーバーへ未送信の報告。オンライン復帰時に再送する。 */
export async function loadUnsyncedReports(): Promise<MistranslationReport[]> {
  const all = await loadReports();
  return all.filter((r) => !r.synced);
}

export async function markReportSynced(id: string): Promise<void> {
  const database = await db();
  const report = await database.get('reports', id);
  if (!report) return;
  await database.put('reports', { ...report, synced: true });
}

export async function clearReports(): Promise<void> {
  const database = await db();
  await database.clear('reports');
}

export async function saveSetting<T>(key: string, value: T): Promise<void> {
  const database = await db();
  await database.put('settings', value, key);
}

export async function loadSetting<T>(key: string): Promise<T | undefined> {
  const database = await db();
  return (await database.get('settings', key)) as T | undefined;
}
